import API from '@aws-amplify/api';
import { EntityWithId, IClient } from './interface';
import { getError } from 'core/errors/errorHelpers';

export interface Options<T, Req = T, Res = Req, ListRes = Res[]> {
  url?: string;
  mapToRequest: (entity: T) => Req;
  mapFromResponse: (response: Res) => EntityWithId<T>;
  mapFromListResponse?: (response: ListRes) => EntityWithId<T>[];
}

export interface Response<T> {
  statusCode: number;
  data: T;
}

export interface ErrorMessageWrapper {
  message: string;
}

/**
 * @deprecated - use RestApi and its types instead
 *
 * https://github.com/SaleCycle/my-salecycle-com#legacy-code
 */
export interface ErrorResponse {
  response: {
    status: number;
    data: {
      message?: string; // This should be consolidated/fixed when we can agree on
      errorMessage?: string; // consistent error format.
      errors?: string[] | ErrorMessageWrapper[];
    };
  };
}

/**
 * @deprecated https://github.com/SaleCycle/my-salecycle-com#legacy-code
 */
export class Client<T, Req = T, Res = T, ListRes = Res[]> implements IClient<T> {
  private mapToRequest: (entity: T) => Req;
  private mapFromResponse: (response: Res) => EntityWithId<T>;
  private mapFromListResponse: (response: ListRes) => EntityWithId<T>[];

  public constructor(options: Options<T, Req, Res, ListRes>) {
    this.mapToRequest = options.mapToRequest;
    this.mapFromResponse = options.mapFromResponse;
    this.mapFromListResponse = options.mapFromListResponse;
  }

  /**
   * @deprecated https://github.com/SaleCycle/my-salecycle-com#legacy-code
   */
  public post = (endpoint: string, entity: T): Promise<EntityWithId<T>> =>
    API.post(import.meta.env.VITE_API_GATEWAY_NAME, endpoint, {
      body: this.mapToRequest(entity),
      response: true,
    })
      .then(this.handleResponse)
      .catch(this.handleError);

  /**
   * @deprecated https://github.com/SaleCycle/my-salecycle-com#legacy-code
   */
  public get = (endpoint: string, query?: { [key: string]: string }): Promise<EntityWithId<T>> =>
    API.get(import.meta.env.VITE_API_GATEWAY_NAME, endpoint, {
      queryStringParameters: query,
      response: true,
    })
      .then(this.handleResponse)
      .catch(this.handleError);

  /**
   * @deprecated https://github.com/SaleCycle/my-salecycle-com#legacy-code
   */
  public getJson = (endpoint: string, query?: { [key: string]: string }): Promise<T> =>
    API.get(import.meta.env.VITE_API_GATEWAY_NAME, endpoint, {
      queryStringParameters: query,
      response: true,
    })
      .then(response => Promise.resolve(response.data))
      .catch(this.handleError);

  /**
   * @deprecated https://github.com/SaleCycle/my-salecycle-com#legacy-code
   */
  public getCollection = (endpoint: string, query?: { [key: string]: string }): Promise<EntityWithId<T>[]> =>
    API.get(import.meta.env.VITE_API_GATEWAY_NAME, `${endpoint}`, {
      queryStringParameters: query,
      response: true,
    })
      .then(this.handleListResponse)
      .catch(this.handleError);

  /**
   * @deprecated https://github.com/SaleCycle/my-salecycle-com#legacy-code
   */
  public put = (endpoint: string, entity: T): Promise<EntityWithId<T>> =>
    API.put(import.meta.env.VITE_API_GATEWAY_NAME, endpoint, {
      body: this.mapToRequest(entity),
      response: true,
    })
      .then(this.handleResponse)
      .catch(this.handleError);

  /**
   * @deprecated https://github.com/SaleCycle/my-salecycle-com#legacy-code
   */
  public patch = (endpoint: string, changes: { [key: string]: any }): Promise<EntityWithId<T>> =>
    API.patch(import.meta.env.VITE_API_GATEWAY_NAME, endpoint, {
      body: changes,
      response: true,
    })
      .then(this.handleResponse)
      .catch(this.handleError);

  /**
   * @deprecated https://github.com/SaleCycle/my-salecycle-com#legacy-code
   */
  public delete = async (endpoint: string, changes?: { [key: string]: any }): Promise<void> => {
    return await API.del(import.meta.env.VITE_API_GATEWAY_NAME, endpoint, {
      response: true,
      body: changes,
    });
  };

  protected handleResponse = (response: Response<Res>): Promise<EntityWithId<T>> =>
    Promise.resolve(this.mapFromResponse(response.data));

  protected handleListResponse = (response: Response<ListRes>): Promise<EntityWithId<T>[]> => {
    if (!!this.mapFromListResponse) {
      return Promise.resolve(this.mapFromListResponse(response.data));
    }

    if (response.data instanceof Array) {
      return Promise.resolve(response.data.map(item => this.mapFromResponse(item)));
    }

    return Promise.reject('List response not returned');
  };

  protected handleError = (error: ErrorResponse): Promise<never> => {
    if (!error.response) {
      return Promise.reject(error);
    }
    return Promise.reject(getError(error));
  };
}
